import { FilterList } from '@mui/icons-material';
import { FormControl, IconButton, InputLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { type MovieSortBy, ScreenType, type TvSortBy } from '@tmdb-graphql-api/schema/types';
import { isArray } from 'lodash-es';
import { useRouter, useSearchParams } from 'next/navigation.js';
import { useState } from 'react';
import { Route } from '#enums/routes.ts';
import { mapScreenTypeToDiscoverTypeRoute } from '#helpers/enumMappers.ts';
import { movieOptions, tvOptions } from '#helpers/sortAndFilterOptions.ts';
import { useDiscoverFiltersParam } from '#hooks/useDiscoverFiltersParam.ts';
import { useDiscoverSortByParam } from '#hooks/useDiscoverSortByParam.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { FiltersDrawer } from '../FiltersDrawer/index.tsx';
import { Select } from '../Select/index.tsx';
import { Header } from './styled.ts';

const screenSortFilterOptions = {
  [ScreenType.Movie]: movieOptions,
  [ScreenType.Tv]: tvOptions,
};

export type DiscoverHeaderProps = {
  type: ScreenType;
};

export const DiscoverHeader = ({ type }: DiscoverHeaderProps) => {
  const router = useRouter();
  const { uaParser } = useToolbelt();
  const searchParams = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(searchParams.get('filtersOpen') === 'true');
  const [sortByParam, setDiscoverSortByParam] = useDiscoverSortByParam();
  const [filtersParam, setFiltersParam] = useDiscoverFiltersParam();
  const sortFilterOptions = screenSortFilterOptions[type];

  return (
    <>
      <FiltersDrawer
        filtersParam={filtersParam}
        open={filtersOpen}
        setFiltersParam={setFiltersParam}
        setOpen={setFiltersOpen}
        title="Filters"
        type={type}
      />
      <Header>
        <IconButton
          aria-label="Filter results"
          edge="end"
          onClick={() => {
            setFiltersOpen(true);
          }}
          sx={{ color: theme => theme.palette.grey[900], marginLeft: theme => theme.spacing(-1) }}
        >
          <FilterList />
        </IconButton>
        <span>
          <ToggleButtonGroup
            aria-label="Choose media type"
            exclusive
            onChange={(_event, newScreenType?: ScreenType) => {
              if (newScreenType) {
                router.push(`${Route.Discover}/${mapScreenTypeToDiscoverTypeRoute(newScreenType)}`);
              }
            }}
            value={type}
          >
            <ToggleButton aria-label="movies" size="small" sx={{ lineHeight: 'normal' }} value={ScreenType.Movie}>
              Movies
            </ToggleButton>
            <ToggleButton aria-label="tv" size="small" sx={{ lineHeight: 'normal' }} value={ScreenType.Tv}>
              TV
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControl sx={{ marginLeft: theme => theme.spacing(1) }}>
            <InputLabel id="sort-by" sx={{ display: 'none' }}>
              Sort by
            </InputLabel>
            <Select
              id="sort-by-select"
              label="Sort by"
              labelId="sort-by-select-label"
              native={!!uaParser.device.type}
              onChange={event => {
                // Not possible to type the target.value any other way
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                setDiscoverSortByParam(event.target.value as TvSortBy | MovieSortBy);
              }}
              options={Object.keys(sortFilterOptions).map(key => {
                // It is not possible for this to be undefined
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const option = sortFilterOptions[key]!;
                const text = isArray(option) ? option[0] : option;
                return [key, text];
              })}
              sx={{
                '.MuiInputBase-input': {
                  paddingBottom: theme => theme.spacing(0.5),
                  paddingTop: theme => theme.spacing(0.5),
                },
                '.MuiOutlinedInput-notchedOutline': {
                  top: 0,
                },
                legend: { display: 'none' },
              }}
              value={sortByParam}
            />
          </FormControl>
        </span>
      </Header>
    </>
  );
};
